import type {
  FlowAction,
  FlowActionResult,
  FlowActionResultStatus,
  FlowCondition,
  FlowConditionField,
  FlowConditionOperator,
  FlowRuleDraft,
  FlowRunStatus,
} from "./types";

export const FLOW_FIELD_LABELS = {
  url: "网址",
  title: "标题",
  format: "类型",
} satisfies Record<FlowConditionField, string>;

export const FLOW_OPERATOR_LABELS = {
  contains: "包含",
  equals: "等于",
  starts_with: "开头是",
  ends_with: "结尾是",
  in: "属于任一",
  not_in: "不属于任一",
} satisfies Record<FlowConditionOperator, string>;

export const FLOW_OPERATORS_BY_FIELD: Record<
  FlowConditionField,
  readonly FlowConditionOperator[]
> = {
  url: ["contains", "equals", "starts_with", "ends_with", "in", "not_in"],
  title: ["contains", "equals", "starts_with", "ends_with", "in", "not_in"],
  format: ["equals", "in", "not_in"],
};

export const FLOW_FORMAT_LABELS: Record<string, string> = {
  website: "网页",
  image: "图片",
  text: "笔记",
  pdf: "PDF",
  video: "视频",
};

export const FLOW_ACTION_LABELS = {
  move_to_folder: "移动到文件夹",
  add_tags: "添加标签",
} satisfies Record<FlowAction["type"], string>;

export const FLOW_RUN_STATUS_LABELS = {
  succeeded: "成功",
  succeeded_with_skips: "成功，有跳过",
  partially_failed: "部分失败",
  failed: "失败",
} satisfies Record<FlowRunStatus, string>;

export const FLOW_ACTION_STATUS_LABELS = {
  succeeded: "成功",
  failed: "失败",
  skipped_already_done: "已经完成，跳过",
  stopped: "已停止后续匹配",
} satisfies Record<FlowActionResultStatus, string>;

export const MULTI_VALUE_OPERATORS = new Set<FlowConditionOperator>(["in", "not_in"]);

export const FLOW_FORMAT_OPTIONS = Object.entries(FLOW_FORMAT_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export interface FlowRuleValidationErrors {
  name?: string;
  conditions?: Record<number, string>;
  actions?: Record<number, string>;
}

export interface FlowDescriptionContext {
  folders?: Record<string, string>;
  tags?: Record<string, string>;
}

export function createEmptyFlowRuleDraft(): FlowRuleDraft {
  return {
    name: "",
    enabled: true,
    condition_logic: "all",
    conditions: [{ field: "url", operator: "contains", value: "" }],
    actions: [{ type: "move_to_folder", folder_id: "" }],
    stop_on_match: false,
  };
}

export function normalizeFlowRuleDraft(draft: FlowRuleDraft): FlowRuleDraft {
  return {
    ...draft,
    name: draft.name.trim(),
    conditions: draft.conditions.map((condition) => {
      if (MULTI_VALUE_OPERATORS.has(condition.operator)) {
        return {
          field: condition.field,
          operator: condition.operator,
          values: (condition.values ?? []).map((value) => value.trim()).filter(Boolean),
        };
      }

      return {
        field: condition.field,
        operator: condition.operator,
        value: condition.value?.trim() ?? "",
      };
    }),
    actions: draft.actions.map((action) =>
      action.type === "move_to_folder"
        ? { type: "move_to_folder", folder_id: action.folder_id }
        : { type: "add_tags", tag_ids: action.tag_ids.filter(Boolean) },
    ),
  };
}

export function validateFlowRuleDraft(draft: FlowRuleDraft): FlowRuleValidationErrors {
  const normalized = normalizeFlowRuleDraft(draft);
  const errors: FlowRuleValidationErrors = {};

  if (!normalized.name) {
    errors.name = "请输入流程名称";
  }

  normalized.conditions.forEach((condition, index) => {
    const allowed = FLOW_OPERATORS_BY_FIELD[condition.field];
    if (!allowed.includes(condition.operator)) {
      errors.conditions ??= {};
      errors.conditions[index] = "当前字段不支持这个条件";
      return;
    }

    const hasValue = MULTI_VALUE_OPERATORS.has(condition.operator)
      ? Boolean(condition.values?.length)
      : Boolean(condition.value);
    if (!hasValue) {
      errors.conditions ??= {};
      errors.conditions[index] = "请填写条件值";
    }
  });

  normalized.actions.forEach((action, index) => {
    const message = action.type === "move_to_folder"
      ? action.folder_id ? undefined : "请选择文件夹"
      : action.tag_ids.length ? undefined : "请至少选择一个标签";
    if (message) {
      errors.actions ??= {};
      errors.actions[index] = message;
    }
  });

  return errors;
}

export function hasFlowRuleValidationErrors(errors: FlowRuleValidationErrors) {
  return Boolean(
    errors.name ||
    Object.keys(errors.conditions ?? {}).length ||
    Object.keys(errors.actions ?? {}).length,
  );
}

export function describeFlowCondition(condition: FlowCondition) {
  const rawValues = MULTI_VALUE_OPERATORS.has(condition.operator)
    ? condition.values ?? []
    : condition.value ? [condition.value] : [];
  const values = rawValues.map((value) =>
    condition.field === "format" ? FLOW_FORMAT_LABELS[value] ?? value : value,
  );

  return `${FLOW_FIELD_LABELS[condition.field]}${FLOW_OPERATOR_LABELS[condition.operator]}${values.join("、") || "未设置"}`;
}

export function describeFlowAction(
  action: FlowAction,
  context: FlowDescriptionContext = {},
) {
  if (action.type === "move_to_folder") {
    return `移动到「${context.folders?.[action.folder_id] ?? "未选择文件夹"}」`;
  }

  const names = action.tag_ids.map((id) => formatFlowTagName(context.tags?.[id] ?? id));
  return `添加标签 ${names.join("、") || "未选择标签"}`;
}

export function formatFlowTagName(name: string) {
  return name.startsWith("#") ? name : `#${name}`;
}

export function getFlowActionResultTarget(result: FlowActionResult) {
  const names = result.target_names?.length
    ? result.target_names
    : result.target_name ? [result.target_name] : [];

  return result.type === "add_tags"
    ? names.map(formatFlowTagName)
    : names;
}

export function describeFlowRule(
  draft: Pick<FlowRuleDraft, "conditions" | "actions">,
  context: FlowDescriptionContext = {},
) {
  const condition = draft.conditions[0]
    ? describeFlowCondition(draft.conditions[0])
    : "未设置条件";
  const action = draft.actions[0]
    ? describeFlowAction(draft.actions[0], context)
    : "未设置动作";
  const conditionExtra = draft.conditions.length > 1 ? ` 等 ${draft.conditions.length} 个条件` : "";
  const actionExtra = draft.actions.length > 1 ? ` 等 ${draft.actions.length} 个动作` : "";
  return `${condition}${conditionExtra} → ${action}${actionExtra}`;
}

export function generateFlowRuleName(
  draft: Pick<FlowRuleDraft, "conditions" | "actions">,
  context: FlowDescriptionContext = {},
) {
  return describeFlowRule(draft, context).replace(" → ", "，").slice(0, 80);
}

export function moveArrayItem<T>(items: T[], from: number, to: number): T[] {
  if (
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to >= items.length ||
    from === to
  ) {
    return items;
  }

  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
